import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Truck, Clock, MapPin, Satellite, Ruler, Loader2 } from 'lucide-react';

// Centre par défaut : Côte d'Ivoire (Yamoussoukro)
const CI_CENTER = [7.6884, -5.0301];

// Crée une icône SVG circulaire avec lettre / numéro
const buildIcon = (color, label) => L.divIcon({
  className: 'route-preview-marker',
  html: `
    <div style="
      width:30px;height:30px;border-radius:50%;
      background:${color};color:white;
      display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:13px;font-family:system-ui,sans-serif;
      border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);
    ">${label}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -16],
});

const FitBounds = ({ bounds }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!bounds || bounds.length < 2) return;
    const t = setTimeout(() => {
      try {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11, animate: true });
      } catch { /* noop */ }
    }, 150);
    return () => clearTimeout(t);
  }, [JSON.stringify(bounds), map]);
  return null;
};

const formatDuration = (seconds) => {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
  return `${m} min`;
};

/**
 * Aperçu d'itinéraire pour le wizard de transfert.
 *
 * Props:
 *  - departure: { lat, lng } | null
 *  - arrival: { lat, lng } | null
 *  - intermediates: [{ address, coordinates: { lat, lng } }, ...]
 *  - polyline: [[lat, lng], ...]  (geometry OSRM)
 *  - meta: { distance_km, duration_s, segments, is_fallback } | null
 *  - loading: bool
 *  - height?: string  (default 500px)
 */
const RoutePreviewMap = ({
  departure = null,
  arrival = null,
  intermediates = [],
  polyline = [],
  meta = null,
  loading = false,
  height = '500px',
}) => {
  const validIntermediates = (intermediates || []).filter(
    (s) => s?.coordinates?.lat != null && s?.coordinates?.lng != null,
  );

  // Markers
  const markers = useMemo(() => {
    const arr = [];
    if (departure?.lat != null && departure?.lng != null) {
      arr.push({
        pos: [departure.lat, departure.lng],
        icon: buildIcon('#10b981', 'A'),
        label: 'Départ',
        addr: departure.address,
      });
    }
    validIntermediates.forEach((s, i) => {
      const letter = String.fromCharCode(66 + i); // B, C, D, ...
      arr.push({
        pos: [s.coordinates.lat, s.coordinates.lng],
        icon: buildIcon('#3b82f6', letter),
        label: `Étape ${i + 1}`,
        addr: s.address,
      });
    });
    if (arrival?.lat != null && arrival?.lng != null) {
      arr.push({
        pos: [arrival.lat, arrival.lng],
        icon: buildIcon('#ef4444', 'Z'),
        label: 'Arrivée',
        addr: arrival.address,
      });
    }
    return arr;
  }, [departure, arrival, validIntermediates]);

  // Bounds : tous les markers + polyline
  const bounds = useMemo(() => {
    const pts = markers.map((m) => m.pos);
    if (polyline?.length > 1) pts.push(...polyline);
    return pts.length >= 2 ? pts : null;
  }, [markers, polyline]);

  const hasMinimum = (departure && arrival);
  const showOverlay = meta && hasMinimum;

  return (
    <div
      className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
      style={{ height }}
      data-testid="route-preview-map"
    >
      {/* Empty state — aucune adresse encore */}
      {!hasMinimum && !loading && (
        <div className="absolute inset-0 z-[450] flex items-center justify-center bg-slate-50/95 backdrop-blur-sm pointer-events-none">
          <div className="text-center max-w-sm px-4">
            <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              Saisissez une adresse de <span className="font-medium text-slate-700">départ</span> et
              d'<span className="font-medium text-slate-700">arrivée</span> pour voir le trajet.
            </p>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && hasMinimum && (
        <div className="absolute top-3 left-3 z-[500] bg-white/95 backdrop-blur shadow-md rounded-lg px-3 py-2 text-xs flex items-center gap-2 border border-slate-200">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
          <span className="text-slate-600">Calcul de l'itinéraire…</span>
        </div>
      )}

      <MapContainer
        center={departure ? [departure.lat, departure.lng] : CI_CENTER}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='© OpenStreetMap'
        />

        {markers.map((m, i) => (
          <Marker key={`m-${i}`} position={m.pos} icon={m.icon}>
            <Popup>
              <div className="text-xs">
                <p className="font-semibold mb-0.5">{m.label}</p>
                <p className="text-slate-600">{m.addr || '—'}</p>
              </div>
            </Popup>
          </Marker>
        ))}

        {polyline?.length > 1 && (
          <Polyline
            positions={polyline}
            pathOptions={{
              color: meta?.is_fallback ? '#f59e0b' : '#10b981',
              weight: 4,
              opacity: 0.85,
              dashArray: meta?.is_fallback ? '8, 8' : null,
            }}
          />
        )}

        {bounds && <FitBounds bounds={bounds} />}
      </MapContainer>

      {/* Overlay info-bulle */}
      {showOverlay && (
        <div
          className="absolute bottom-3 right-3 z-[500] bg-white/95 backdrop-blur shadow-lg rounded-lg p-3 border border-slate-200 min-w-[180px]"
          data-testid="route-preview-overlay"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Truck className="w-4 h-4 text-emerald-600" />
            <span>{Math.round(meta.distance_km)} km{meta.is_round_trip ? ' (A/R)' : ''}</span>
          </div>
          {meta.is_round_trip && meta.distance_one_way && (
            <div className="text-[11px] text-slate-500 ml-6 -mt-0.5" data-testid="route-round-trip-detail">
              {Math.round(meta.distance_one_way)} km aller × 2
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-slate-600 mt-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{formatDuration(meta.duration_s)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
            <MapPin className="w-3.5 h-3.5" />
            <span>{meta.segments} segment{meta.segments > 1 ? 's' : ''}</span>
          </div>
          <div className="border-t border-slate-200 mt-2 pt-1.5 flex items-center gap-1.5 text-[11px]">
            {meta.is_fallback ? (
              <>
                <Ruler className="w-3 h-3 text-amber-600" />
                <span className="text-amber-700 font-medium" title="OSRM indisponible — distance approximative ±15%">
                  Estimation
                </span>
              </>
            ) : (
              <>
                <Satellite className="w-3 h-3 text-emerald-600" />
                <span className="text-emerald-700 font-medium">OSRM (précis)</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutePreviewMap;
